/**
 * Unified document analysis StateField — the single source of truth for all
 * document-structure queries in the editor.
 *
 * Previously, every subsystem (syntax decorations, auto-fold, diagnostics)
 * independently scanned the entire document on every keystroke or cursor move.
 * This StateField centralises all scanning into ONE computation per doc change,
 * and all consumers read from the cached result.
 *
 * Eliminated redundant work:
 *  - scanMagicBlocks  × 2  (init) + × 1 (per keystroke)  → × 1 (per doc change)
 *  - scanCompleteBlocks × 1 (per keystroke in autoFold)    → derived from scan
 *  - findMetaBlocks + findContentBlocks + findUseBlocks    → read from scan
 *    + findOutputFenceLines (4 scans per cursor move in diagnostics)
 */

import { type EditorState, type Text, StateField } from "@codemirror/state";
import { type MagicBlockScan, scanMagicBlocks } from "./build-decorations";
import {
	BLOCK_RENDER_RE,
	FOR_CLOSE_RE,
	FOR_HEADER_RE,
	parseMarkdownFenceOpen,
} from "./patterns";

/* ── Public types ── */

export interface RenderBlockInfo {
	/** Render id (e.g. "totalSales") */
	id: string;
	/** 1-based line number */
	line: number;
	/** Document positions (start of line .. end of line) */
	from: number;
	to: number;
}

export interface MagicDocumentAnalysis {
	/** Full block scan (define, meta, import, use, content blocks with line indices). */
	scan: MagicBlockScan;
	/** Active standalone `::` line that is treated as draft input, not a content closer. */
	draftContentCloseLine: number | null;
	/** Fold ranges keyed by block identifier — consumed by autoFoldPlugin. */
	foldBlocks: Map<string, { from: number; to: number }>;
	/** 1-based line numbers inside ```output fences — consumed by diagnostics. */
	outputFenceLines: Set<number>;
	/** Standalone `::render[id]::` lines (block-form, not inline). */
	renderBlocks: RenderBlockInfo[];
}

/* ── StateField ── */

export const magicScanField = StateField.define<MagicDocumentAnalysis>({
	create(state) {
		return computeAnalysis(state);
	},
	update(analysis, tr) {
		const draftContentCloseLine = getDraftContentCloseLine(tr.state);
		// Reuse cached analysis when neither document nor draft-close context changed.
		if (
			!tr.docChanged &&
			draftContentCloseLine === analysis.draftContentCloseLine
		) {
			return analysis;
		}
		return computeAnalysis(tr.state);
	},
});

/* ── Core computation (runs at most once per doc change) ── */

function computeAnalysis(state: EditorState): MagicDocumentAnalysis {
	const doc = state.doc;
	const candidateDraftContentCloseLine = getDraftContentCloseLine(state);
	let draftContentCloseLine: number | null = null;
	let scan: MagicBlockScan;
	if (candidateDraftContentCloseLine === null) {
		scan = scanMagicBlocks(doc);
	} else {
		const draftScan = scanMagicBlocks(doc, {
			ignoreContentCloseLines: new Set([candidateDraftContentCloseLine]),
		});
		const draftContentBlock =
			draftScan.contentBlockByLine[candidateDraftContentCloseLine];
		if (
			draftContentBlock &&
			draftContentBlock.startLine < candidateDraftContentCloseLine &&
			draftContentBlock.endLine > candidateDraftContentCloseLine
		) {
			draftContentCloseLine = candidateDraftContentCloseLine;
			scan = draftScan;
		} else {
			scan = scanMagicBlocks(doc);
		}
	}
	const foldBlocks = computeFoldBlocks(scan, doc);
	const outputFenceLines = scan.outputFenceLines;
	const renderBlocks = scanRenderBlocks(doc, scan);
	return { scan, draftContentCloseLine, foldBlocks, outputFenceLines, renderBlocks };
}

function getDraftContentCloseLine(state: EditorState): number | null {
	const { main } = state.selection;
	if (!main.empty) return null;
	const line = state.doc.lineAt(main.head);
	return /^::\s*$/.test(line.text.trim()) ? line.number : null;
}

/* ── Standalone render-block scan ── */

/**
 * Find lines that match `^::render[id]::$` and lie OUTSIDE any other magic
 * block (define / meta / content / use / import body) and outside any markdown
 * code fence. These are draggable as their own units.
 */
function scanRenderBlocks(doc: Text, scan: MagicBlockScan): RenderBlockInfo[] {
	const result: RenderBlockInfo[] = [];
	const skipMask = buildInnerBlockLineMask(scan, doc.lines);

	let mdFence: { char: "`" | "~"; length: number } | null = null;
	for (let i = 1; i <= doc.lines; i++) {
		const lineObj = doc.line(i);
		const text = lineObj.text;
		const trimmed = text.trim();

		if (mdFence) {
			if (
				trimmed.length >= mdFence.length &&
				trimmed === mdFence.char.repeat(trimmed.length)
			) {
				mdFence = null;
			}
			continue;
		}
		// Detect top-level markdown fence open/close
		const fenceOpen = parseMarkdownFenceOpen(trimmed);
		if (fenceOpen) {
			mdFence = { char: fenceOpen.char, length: fenceOpen.length };
			continue;
		}
		if (skipMask[i]) continue;

		const m = trimmed.match(BLOCK_RENDER_RE);
		if (m) {
			result.push({
				id: m[1],
				line: i,
				from: lineObj.from,
				to: lineObj.to,
			});
		}
	}
	return result;
}

/**
 * Build a 1-based line mask where mask[i] = true if line i sits INSIDE the
 * body of any magic block (define / meta / content / use / import / for).
 * The header line and close line of each block are NOT marked (they're the
 * block's own anchors, not "inner" lines).
 */
function buildInnerBlockLineMask(
	scan: MagicBlockScan,
	totalLines: number,
): boolean[] {
	const mask = new Array<boolean>(totalLines + 2).fill(false);
	const markRange = (start: number, end: number) => {
		for (let i = start + 1; i < end; i++) mask[i] = true;
	};
	for (const b of scan.defineBlocks) markRange(b.startLine, b.endLine);
	for (const b of scan.metaBlocks) markRange(b.startLine, b.endLine);
	for (const b of scan.contentBlocks) markRange(b.startLine, b.endLine);
	for (const b of scan.useBlocks) markRange(b.startLine, b.endLine);
	return mask;
}

/* ── Fold-block derivation ── */

/**
 * Derive fold ranges from the scan data (O(blocks)) and supplement with a
 * targeted scan for ::for blocks (which aren't tracked by scanMagicBlocks).
 */
function computeFoldBlocks(
	scan: MagicBlockScan,
	doc: Text,
): Map<string, { from: number; to: number }> {
	const blocks = new Map<string, { from: number; to: number }>();

	// Define blocks
	for (const b of scan.defineBlocks) {
		blocks.set(b.defId, {
			from: doc.line(b.startLine).to,
			to: doc.line(b.endLine).to,
		});
	}

	// Meta blocks
	for (const b of scan.metaBlocks) {
		blocks.set(`meta:${b.domain}`, {
			from: doc.line(b.startLine).to,
			to: doc.line(b.endLine).to,
		});
	}

	// Use blocks
	for (const b of scan.useBlocks) {
		blocks.set(`use:${b.useId}`, {
			from: doc.line(b.startLine).to,
			to: doc.line(b.endLine).to,
		});
	}

	// Content blocks
	for (const b of scan.contentBlocks) {
		blocks.set(`content:${b.defId}`, {
			from: doc.line(b.startLine).to,
			to: doc.line(b.endLine).to,
		});
	}

	// Import block-form: import lines whose namespace is known
	// Block-form imports carry their parsed end line from scanMagicBlocks.
	for (const imp of scan.importLines) {
		if (!imp.namespace) continue;
		if (imp.endLine > imp.startLine) {
			blocks.set(`import:${imp.namespace}`, {
				from: doc.line(imp.startLine).to,
				to: doc.line(imp.endLine).to,
			});
		}
	}

	// For blocks: stack-based scan, skipping lines inside other magic blocks
	// (their fences would otherwise confuse markdown-fence detection).
	scanForBlockFolds(doc, blocks, scan);

	return blocks;
}

/**
 * Scan for ::for / ::endfor pairs and add their fold ranges.
 * Uses a fast first-character check to skip non-directive lines in O(1).
 *
 * Lines inside other magic blocks (define / meta / content / use bodies)
 * are skipped so their inner ```config / ```output / closing ``` fences
 * don't confuse the markdown-fence tracker. ::for blocks themselves are
 * tracked via the stack — nested ::for is not currently expected.
 */
function scanForBlockFolds(
	doc: Text,
	blocks: Map<string, { from: number; to: number }>,
	scan: MagicBlockScan,
): void {
	const stack: { key: string; from: number }[] = [];
	let mdFence: { char: "`" | "~"; length: number } | null = null;
	const skipMask = buildInnerBlockLineMask(scan, doc.lines);

	for (let i = 1; i <= doc.lines; i++) {
		const text = doc.line(i).text;

		// 围栏内：仅检查关闭，跳过 ::for/::endfor 识别
		if (mdFence) {
			const trimmedLine = text.trim();
			if (
				trimmedLine.length >= mdFence.length &&
				trimmedLine === mdFence.char.repeat(trimmedLine.length)
			) {
				mdFence = null;
			}
			continue;
		}

		// 跳过位于其它 magic 块体内的行（其内部 ```config / ```output / 关闭 ```
		// 可能错误地触发 markdown 围栏跟踪）
		if (skipMask[i]) continue;

		// Fast exit: directive lines start with `:`; fence lines start with `\`` or `~`.
		let j = 0;
		while (j < text.length && (text.charCodeAt(j) === 32 || text.charCodeAt(j) === 9)) j++;
		if (j >= text.length) continue;
		const fc = text.charCodeAt(j);
		if (fc !== 58 /* ':' */ && fc !== 96 /* '`' */ && fc !== 126 /* '~' */) continue;

		const trimmed = text.trim();

		// 检测顶层 markdown 围栏开启
		if (fc === 96 || fc === 126) {
			const fenceOpen = parseMarkdownFenceOpen(trimmed);
			if (fenceOpen) {
				mdFence = { char: fenceOpen.char, length: fenceOpen.length };
			}
			continue;
		}

		const forMatch = trimmed.match(FOR_HEADER_RE);
		if (forMatch) {
			stack.push({ key: `for:${forMatch[1]}`, from: doc.line(i).to });
			continue;
		}

		if (FOR_CLOSE_RE.test(trimmed) && stack.length > 0) {
			const top = stack.pop()!;
			blocks.set(top.key, { from: top.from, to: doc.line(i).to });
		}
	}
}
